import React, { createContext, useContext, useEffect, useState } from "react";
import CategoryIcon from "@mui/icons-material/Category";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExploreIcon from "@mui/icons-material/Explore";
import ExtensionIcon from "@mui/icons-material/Extension";
import GridOnIcon from "@mui/icons-material/GridOn";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import PaletteIcon from "@mui/icons-material/Palette";
import StarsIcon from "@mui/icons-material/Stars";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
    getImageUrl,
    getStoryTitle,
    loadScreenshotGallery,
    loadScreenshotJigsaw,
} from "../lib/api";
import { JIGSAW_APP_ICON_ART } from "../pages/AppIconPage";
import { getScreenshotScene } from "../screenshotScenes";
import { jigsawShellBgForScene } from "../styles/screenshotStyles";

const VAULT_PACK_FALLBACKS = ["Epic Dragons", "Impressionism", "Ocean Depths"];

function useVaultPackCovers(scene) {
    const [packs, setPacks] = useState([]);
    useEffect(() => {
        let cancelled = false;
        loadScreenshotGallery(scene)
            .then((items) => {
                if (cancelled) return;
                const next = (items || [])
                    .map((item, i) => {
                        const imageUrl = getImageUrl(item?.group?.story);
                        if (!imageUrl) return null;
                        return {
                            title:
                                getStoryTitle(item?.group?.story) ||
                                VAULT_PACK_FALLBACKS[i] ||
                                "Puzzle Pack",
                            imageUrl,
                        };
                    })
                    .filter(Boolean)
                    .slice(0, 3);
                setPacks(next);
            })
            .catch(() => {
                if (!cancelled) setPacks([]);
            });
        return () => {
            cancelled = true;
        };
    }, [scene]);
    return packs;
}

export const SQUARE_AD_W = 1200;
export const SQUARE_AD_H = 1200;
export const LANDSCAPE_AD_W = 1200;
export const LANDSCAPE_AD_H = 628;

/** @typedef {'landscape' | 'square'} GoogleAdVariant */

const AdVariantContext = createContext(/** @type {GoogleAdVariant} */ ("square"));
const AdLangContext = createContext("en-US");

function useIsSquare() {
    return useContext(AdVariantContext) === "square";
}

const TITLE_STACK =
    '"Segoe UI", "Arial Black", "Helvetica Neue", Impact, sans-serif';
/** Landscape phones ~175% larger; bleed top/bottom so ~central 70% is visible. */
const PHONE_W = 460;
const PHONE_H = 940;
/** Square phones are ~150% larger; frames intentionally clip past the canvas. */
const PHONE_W_SQ = 450;
const PHONE_H_SQ = 930;

function preloadImage(src) {
    return new Promise((resolve) => {
        if (!src) {
            resolve();
            return;
        }
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = src;
    });
}

const PUBLIC = process.env.PUBLIC_URL || "";
const JOURNEY_MAP_BG = `${PUBLIC}/bg_images/word-wheel/journey_map.png`;
const FEATURE_GRAPHIC = `${PUBLIC}/ad/jigsaw-muse-feature-graphic-1024x500.png`;

/** Puzzle cover URLs for a scene — no nested iframes (keeps Google Ads tab from OOMing). */
function useSceneCoverUrls(slug) {
    const [urls, setUrls] = useState([]);
    useEffect(() => {
        let cancelled = false;
        const scene = getScreenshotScene(slug);
        if (!scene) {
            setUrls([]);
            return undefined;
        }
        (async () => {
            try {
                if (scene.layout === "journeyMap" || scene.marketing === "journey") {
                    await preloadImage(JOURNEY_MAP_BG);
                    if (!cancelled) setUrls([JOURNEY_MAP_BG]);
                    return;
                }
                const useGallery =
                    scene.layout === "vaultMenu" ||
                    scene.layout === "twoColumnGrid" ||
                    (Array.isArray(scene.galleryPrepend) && scene.galleryPrepend.length > 0);
                if (useGallery) {
                    const items = await loadScreenshotGallery(scene);
                    if (cancelled) return;
                    const next = items
                        .map((item) => getImageUrl(item?.group?.story))
                        .filter(Boolean);
                    await Promise.all(next.slice(0, 12).map(preloadImage));
                    if (!cancelled) setUrls(next);
                    return;
                }
                if (scene.storyId) {
                    const { group } = await loadScreenshotJigsaw(scene);
                    if (cancelled) return;
                    const url = getImageUrl(group?.story);
                    if (url) await preloadImage(url);
                    if (!cancelled) setUrls(url ? [url] : []);
                    return;
                }
                if (!cancelled) setUrls([]);
            } catch {
                if (!cancelled) setUrls([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [slug]);
    return urls;
}

function lockOverflow() {
    const prevHtml = document.documentElement.style.overflow;
    const prevBody = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
        document.documentElement.style.overflow = prevHtml;
        document.body.style.overflow = prevBody;
    };
}

function AdCanvas({ children, sx = {} }) {
    const variant = useContext(AdVariantContext);
    const isSquare = variant === "square";
    const width = isSquare ? SQUARE_AD_W : LANDSCAPE_AD_W;
    const height = isSquare ? SQUARE_AD_H : LANDSCAPE_AD_H;
    useEffect(() => lockOverflow(), []);
    return (
        <Box
            data-screenshot-frame={
                isSquare ? "jigsaw-square-ad" : "jigsaw-landscape-ad"
            }
            data-google-ad-square={isSquare ? "1" : undefined}
            data-google-ad-landscape={isSquare ? undefined : "1"}
            sx={{
                position: "fixed",
                inset: 0,
                width,
                height,
                overflow: "hidden",
                boxSizing: "border-box",
                zIndex: 1,
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

function ShellFill({ scene }) {
    const bg = jigsawShellBgForScene(scene);
    return (
        <Box
            aria-hidden
            sx={{ position: "absolute", inset: 0, zIndex: 0, background: bg }}
        />
    );
}

function galleryGridDims(count) {
    if (count <= 6) return { cols: 3, rows: 2 };
    if (count <= 9) return { cols: 3, rows: 3 };
    if (count <= 10) return { cols: 5, rows: 2 };
    if (count <= 12) return { cols: 4, rows: 3 };
    const cols = 4;
    return { cols, rows: Math.ceil(count / cols) };
}

/** Full-bleed puzzle artwork (image, not nested iframe — avoids Chrome OOM). */
function SceneBleed({ slug, dim = 0.35, grid = false }) {
    const covers = useSceneCoverUrls(slug);
    const primary = covers[0];
    const { cols, rows } = galleryGridDims(covers.length);
    return (
        <Box aria-hidden sx={{ position: "absolute", inset: 0, zIndex: 0, overflow: "hidden" }}>
            {grid && covers.length > 1 ? (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        gridTemplateColumns: `repeat(${cols}, 1fr)`,
                        gridTemplateRows: `repeat(${rows}, 1fr)`,
                        gap: "3px",
                    }}
                >
                    {covers.slice(0, cols * rows).map((src) => (
                        <Box
                            key={src}
                            component="img"
                            src={src}
                            alt=""
                            sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                        />
                    ))}
                </Box>
            ) : primary ? (
                <Box
                    component="img"
                    src={primary}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                        filter: "saturate(1.1) contrast(1.05)",
                    }}
                />
            ) : (
                <Box sx={{ position: "absolute", inset: 0, bgcolor: "#0f172a" }} />
            )}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `rgba(2,6,23,${dim})`,
                    pointerEvents: "none",
                }}
            />
        </Box>
    );
}

function AdPhone({ rotate = 0, width, height, children, sx = {} }) {
    const isSquare = useIsSquare();
    const w = width ?? (isSquare ? PHONE_W_SQ : PHONE_W);
    const h = height ?? (isSquare ? PHONE_H_SQ : PHONE_H);
    return (
        <Box
            sx={{
                width: w,
                height: h,
                transform: rotate ? `rotate(${rotate}deg)` : "none",
                transformOrigin: "center center",
                borderRadius: "28px",
                p: "5px",
                background:
                    "linear-gradient(160deg, rgba(226,232,240,0.75) 0%, rgba(15,23,42,0.95) 48%, rgba(59,166,102,0.4) 100%)",
                boxShadow:
                    "0 22px 48px rgba(0,0,0,0.45), 0 0 0 1px rgba(167,243,208,0.28)",
                boxSizing: "border-box",
                flexShrink: 0,
                ...sx,
            }}
        >
            <Box
                sx={{
                    borderRadius: "23px",
                    overflow: "hidden",
                    bgcolor: "#0f172a",
                    height: "100%",
                    position: "relative",
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

/** Phone mockup with puzzle cover art (image — not a nested iframe). */
function ShotPhone({ slug, rotate = 0, width, height, sx }) {
    const covers = useSceneCoverUrls(slug);
    const src = covers[0];
    return (
        <AdPhone rotate={rotate} width={width} height={height} sx={sx}>
            {src ? (
                <Box
                    component="img"
                    src={src}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            ) : (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: jigsawShellBgForScene(getScreenshotScene(slug)),
                    }}
                />
            )}
            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: "28%",
                    background:
                        "linear-gradient(180deg, transparent 0%, rgba(2,6,23,0.55) 100%)",
                    pointerEvents: "none",
                }}
            />
        </AdPhone>
    );
}

function AdHeadline({ title, subtitle, dark = false, sx = {} }) {
    const isSquare = useIsSquare();
    return (
        <Box sx={{ position: "relative", zIndex: 3, textAlign: "center", px: 3, ...sx }}>
            <Typography
                component="h1"
                sx={{
                    m: 0,
                    fontFamily: TITLE_STACK,
                    fontWeight: 900,
                    fontSize: isSquare ? 56 : 40,
                    lineHeight: 1.05,
                    letterSpacing: "0.03em",
                    color: dark ? "#0f172a" : "#f8fafc",
                    textShadow: dark ? "none" : "0 3px 18px rgba(2,6,23,0.65)",
                    whiteSpace: "pre-line",
                }}
            >
                {title}
            </Typography>
            {subtitle ? (
                <Typography
                    sx={{
                        m: 0,
                        mt: 1,
                        fontWeight: 600,
                        fontSize: isSquare ? 20 : 17,
                        color: dark ? "#475569" : "rgba(224,242,254,0.92)",
                        textShadow: dark ? "none" : "0 2px 10px rgba(2,6,23,0.5)",
                    }}
                >
                    {subtitle}
                </Typography>
            ) : null}
        </Box>
    );
}

function FloatBadge({ children, sx = {} }) {
    return (
        <Box
            sx={{
                position: "relative",
                zIndex: 3,
                px: 2,
                py: 1.25,
                borderRadius: 2,
                bgcolor: "rgba(15,23,42,0.84)",
                border: "1px solid rgba(110,231,183,0.45)",
                boxShadow: "0 12px 28px rgba(0,0,0,0.35)",
                backdropFilter: "blur(8px)",
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

function PieceTile({ label, sx = {}, blur = 0 }) {
    return (
        <Box
            sx={{
                width: 56,
                height: 56,
                borderRadius: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: TITLE_STACK,
                fontWeight: 900,
                fontSize: 18,
                color: "#ecfdf5",
                background: "linear-gradient(160deg, #34d399 0%, #059669 55%, #065f46 100%)",
                border: "2px solid rgba(167,243,208,0.55)",
                boxShadow: "0 10px 22px rgba(0,0,0,0.4)",
                filter: blur ? `blur(${blur}px)` : "none",
                ...sx,
            }}
        >
            {label}
        </Box>
    );
}

/** Soft glowing connector from phone toward a badge/popup. */
function GlowConnector({ x1, y1, x2, y2 }) {
    return (
        <Box
            component="svg"
            aria-hidden
            sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                zIndex: 2,
                pointerEvents: "none",
            }}
        >
            <defs>
                <linearGradient id="jigsawGlowLine" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(110,231,183,0.15)" />
                    <stop offset="50%" stopColor="rgba(52,211,153,0.95)" />
                    <stop offset="100%" stopColor="rgba(110,231,183,0.2)" />
                </linearGradient>
                <filter id="jigsawGlowBlur" x="-40%" y="-40%" width="180%" height="180%">
                    <feGaussianBlur stdDeviation="4" result="b" />
                    <feMerge>
                        <feMergeNode in="b" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>
            <line
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
                stroke="url(#jigsawGlowLine)"
                strokeWidth="4"
                strokeLinecap="round"
                filter="url(#jigsawGlowBlur)"
            />
            <circle cx={x2} cy={y2} r="7" fill="#6ee7b7" opacity="0.9" />
        </Box>
    );
}

function WoodSurface({ children, sx = {} }) {
    return (
        <Box
            aria-hidden={children ? undefined : true}
            sx={{
                background: `
                    radial-gradient(ellipse at 20% 30%, rgba(180,120,60,0.22), transparent 50%),
                    radial-gradient(ellipse at 80% 70%, rgba(90,50,20,0.28), transparent 45%),
                    repeating-linear-gradient(
                        92deg,
                        #5c3a1e 0px,
                        #6b4423 2px,
                        #4a2f18 4px,
                        #7a5230 7px,
                        #5c3a1e 10px
                    ),
                    linear-gradient(165deg, #3e2723 0%, #5d4037 40%, #4e342e 100%)
                `,
                boxShadow: "inset 0 0 40px rgba(0,0,0,0.35)",
                ...sx,
            }}
        >
            {children}
        </Box>
    );
}

/** 01 — Dual-device hero. */
function ShowcaseHero({ scene }) {
    const isSquare = useIsSquare();
    const floatingPieces = [
        { label: "M", left: 36, top: 120, rot: -18, blur: 0, size: 72 },
        { label: "U", left: 160, top: 70, rot: 12, blur: 1.5, size: 64 },
        { label: "S", left: 980, top: 90, rot: -8, blur: 0, size: 70 },
        { label: "E", left: 1080, top: 220, rot: 16, blur: 2, size: 58 },
        { label: "✦", left: 40, top: 520, rot: 22, blur: 3, size: 52 },
        { label: "◆", left: 1050, top: 560, rot: -14, blur: 2.5, size: 60 },
        { label: "M", left: 90, top: 900, rot: 8, blur: 4, size: 80 },
        { label: "U", left: 1000, top: 940, rot: -20, blur: 5, size: 76 },
    ];

    if (!isSquare) {
        const landscapePieces = [
            { label: "M", left: 48, top: 36, rot: -16, blur: 0, size: 64 },
            { label: "U", left: 140, top: 70, rot: 10, blur: 1.5, size: 56 },
            { label: "S", left: 1000, top: 40, rot: -8, blur: 0, size: 62 },
            { label: "E", left: 1090, top: 90, rot: 14, blur: 2, size: 54 },
            { label: "✦", left: 80, top: 500, rot: 18, blur: 3, size: 48 },
            { label: "◆", left: 1060, top: 480, rot: -12, blur: 2.5, size: 52 },
        ];
        return (
            <AdCanvas>
                <ShellFill scene={scene} />
                {landscapePieces.map((p, i) => (
                    <PieceTile
                        key={`${p.label}-ls-${i}`}
                        label={p.label}
                        blur={p.blur}
                        sx={{
                            position: "absolute",
                            zIndex: 2,
                            left: p.left,
                            top: p.top,
                            width: p.size,
                            height: p.size,
                            transform: `rotate(${p.rot}deg)`,
                            opacity: 0.65,
                            boxShadow:
                                "0 10px 22px rgba(0,0,0,0.4), 0 0 18px rgba(52,211,153,0.45)",
                        }}
                    />
                ))}
                <AdHeadline
                    title={"SAVOR EVERY PIECE"}
                    subtitle="Premium fantasy jigsaws. Crisp interlocking play."
                    sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: 28,
                        pt: 0,
                        zIndex: 4,
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: "52%",
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: 3.5,
                        zIndex: 3,
                    }}
                >
                    <BrandSplashPhone
                        scene={scene}
                        rotate={-10}
                        width={380}
                        height={780}
                    />
                    <ShotPhone slug="01" rotate={9} width={380} height={780} />
                </Box>
            </AdCanvas>
        );
    }

    return (
        <AdCanvas>
            <ShellFill scene={scene} />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: `
                        radial-gradient(ellipse at 50% 80%, rgba(56,189,248,0.16) 0%, transparent 50%),
                        radial-gradient(circle at 10% 90%, rgba(16,185,129,0.12) 0%, transparent 35%)
                    `,
                    pointerEvents: "none",
                }}
            />
            {floatingPieces.map((p, i) => (
                <PieceTile
                    key={`${p.label}-${i}`}
                    label={p.label}
                    blur={p.blur}
                    sx={{
                        position: "absolute",
                        zIndex: 2,
                        left: p.left,
                        top: p.top,
                        width: p.size,
                        height: p.size,
                        transform: `rotate(${p.rot}deg) scale(${1 + (i % 3) * 0.08})`,
                        opacity: 0.55 + (i % 4) * 0.1,
                    }}
                />
            ))}
            <AdHeadline
                title={"SAVOR EVERY\nPIECE"}
                subtitle="Premium fantasy jigsaws. Crisp interlocking play."
                sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 210,
                    pt: 0,
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: 390,
                    transform: "translateX(-50%)",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "flex-start",
                    gap: 3,
                    zIndex: 3,
                }}
            >
                <BrandSplashPhone
                    scene={scene}
                    rotate={-11}
                    width={400}
                    height={840}
                    sx={{ mb: -120 }}
                />
                <ShotPhone
                    slug="01"
                    rotate={10}
                    width={400}
                    height={840}
                    sx={{ mb: -120 }}
                />
            </Box>
        </AdCanvas>
    );
}

function BrandSplashPhone({ scene, rotate = -9, width, height, sx = {} }) {
    return (
        <AdPhone rotate={rotate} width={width} height={height} sx={sx}>
            <Box sx={{ position: "relative", height: "100%" }}>
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        background: jigsawShellBgForScene(scene),
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1,
                        px: 2,
                    }}
                >
                    <Box
                        component="img"
                        src={JIGSAW_APP_ICON_ART}
                        alt=""
                        sx={{
                            width: width && width > 320 ? 130 : 100,
                            height: width && width > 320 ? 130 : 100,
                            borderRadius: "28%",
                            mb: 1.5,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: TITLE_STACK,
                            fontWeight: 900,
                            fontSize: width && width > 320 ? 20 : 16,
                            color: "#ecfdf5",
                            textAlign: "center",
                        }}
                    >
                        JIGSAW
                        <br />
                        MUSE
                    </Typography>
                </Box>
            </Box>
        </AdPhone>
    );
}

/** Enlarged Color Baskets floating UI. */
function ColorBasketsPopup({ sx = {}, wide = false }) {
    const trays = [
        { name: "Ocean", color: "linear-gradient(160deg,#38bdf8,#0369a1)" },
        { name: "Forest", color: "linear-gradient(160deg,#4ade80,#166534)" },
        { name: "Sunset", color: "linear-gradient(160deg,#fb923c,#c2410c)" },
        { name: "Amethyst", color: "linear-gradient(160deg,#c084fc,#6b21a8)" },
    ];
    return (
        <Box
            sx={{
                width: wide ? 480 : 420,
                borderRadius: 3,
                p: wide ? 2.75 : 2.5,
                background:
                    "linear-gradient(165deg, rgba(15,23,42,0.96) 0%, rgba(30,58,138,0.92) 100%)",
                border: "1px solid rgba(110,231,183,0.55)",
                boxShadow:
                    "0 24px 48px rgba(0,0,0,0.5), 0 0 40px rgba(52,211,153,0.25), inset 0 1px 0 rgba(255,255,255,0.08)",
                transform: "perspective(800px) rotateY(-8deg) rotateX(4deg)",
                ...sx,
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <PaletteIcon sx={{ color: "#6ee7b7", fontSize: wide ? 36 : 32 }} />
                <Typography
                    sx={{
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: wide ? 30 : 26,
                        color: "#ecfdf5",
                    }}
                >
                    Color Baskets
                </Typography>
            </Stack>
            <Typography
                sx={{
                    fontSize: wide ? 15 : 14,
                    color: "rgba(226,232,240,0.88)",
                    mb: 2,
                    lineHeight: 1.4,
                }}
            >
                Pick a tray, assemble it, then deploy to the board.
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 1.25,
                }}
            >
                {trays.map((t) => (
                    <Box
                        key={t.name}
                        sx={{
                            height: wide ? 110 : 88,
                            borderRadius: 2,
                            background: t.color,
                            border: "2px solid rgba(255,255,255,0.25)",
                            boxShadow: "0 8px 18px rgba(0,0,0,0.35)",
                            display: "flex",
                            alignItems: "flex-end",
                            p: 1,
                        }}
                    >
                        <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#fff" }}>
                            {t.name}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function StreakTrophyCard({ sx = {}, wide = false }) {
    return (
        <FloatBadge
            sx={{
                width: wide ? 480 : 340,
                textAlign: "center",
                py: wide ? 3.5 : 2.5,
                boxShadow: "0 18px 40px rgba(0,0,0,0.45), 0 0 32px rgba(251,191,36,0.45)",
                border: "1px solid rgba(251,191,36,0.55)",
                transform: wide
                    ? "perspective(800px) rotateY(-6deg) rotateX(3deg)"
                    : undefined,
                ...sx,
            }}
        >
            <EmojiEventsIcon
                sx={{
                    fontSize: wide ? 64 : 56,
                    color: "#fbbf24",
                    filter: "drop-shadow(0 0 12px rgba(251,191,36,0.8))",
                }}
            />
            <Typography
                sx={{
                    mt: 1,
                    fontFamily: TITLE_STACK,
                    fontWeight: 900,
                    fontSize: wide ? 34 : 30,
                    color: "#fde68a",
                }}
            >
                7 Day Streak!
            </Typography>
            <Stack
                direction="row"
                spacing={0.75}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 1 }}
            >
                <LocalFireDepartmentIcon sx={{ color: "#f97316" }} />
                <Typography sx={{ fontWeight: 700, color: "#e2e8f0", fontSize: wide ? 16 : 15 }}>
                    Fresh art every day
                </Typography>
            </Stack>
        </FloatBadge>
    );
}

/** 02 — Color baskets zoom + badge. */
function ShowcaseColorBasket({ scene }) {
    const isSquare = useIsSquare();
    if (!isSquare) {
        return (
            <AdCanvas>
                <ShellFill scene={scene} />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                        background:
                            "radial-gradient(circle at 70% 45%, rgba(52,211,153,0.16) 0%, transparent 42%)",
                        pointerEvents: "none",
                    }}
                />
                <AdHeadline
                    title="COLOR BASKETS"
                    subtitle="Pick a tray, assemble it, then deploy to the board."
                    sx={{ position: "absolute", top: 16, left: 0, right: 0, pt: 0, zIndex: 4 }}
                />
                <GlowConnector x1={520} y1={340} x2={700} y2={320} />
                <Box
                    sx={{
                        position: "absolute",
                        left: 120,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 3,
                    }}
                >
                    <ShotPhone slug="02" rotate={-3} width={480} height={980} />
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        right: 40,
                        top: "52%",
                        transform: "translateY(-50%)",
                        zIndex: 4,
                    }}
                >
                    <ColorBasketsPopup wide />
                </Box>
            </AdCanvas>
        );
    }

    return (
        <AdCanvas>
            <ShellFill scene={scene} />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: `
                        radial-gradient(circle at 70% 40%, rgba(52,211,153,0.14) 0%, transparent 40%),
                        url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 20h18v18H10z' fill='%2334d399' fill-opacity='0.04'/%3E%3C/svg%3E")
                    `,
                    pointerEvents: "none",
                }}
            />
            <AdHeadline
                title="COLOR BASKETS"
                subtitle="Pick a tray, assemble it, then deploy to the board."
                sx={{ pt: 4 }}
            />
            <GlowConnector x1={520} y1={640} x2={720} y2={480} />
            <Box sx={{ position: "absolute", left: 48, bottom: -80, zIndex: 3 }}>
                <ShotPhone slug="02" rotate={-4} width={480} height={980} />
            </Box>
            <Box sx={{ position: "absolute", right: 48, top: 340, zIndex: 4 }}>
                <ColorBasketsPopup />
            </Box>
        </AdCanvas>
    );
}

/** Wooden sorting tray focal. */
function SortingTrayFocal({ sx = {}, wide = false }) {
    const pieces = [
        { left: 40, top: 50, rot: -12, c: "#34d399" },
        { left: 120, top: 36, rot: 8, c: "#60a5fa" },
        { left: 200, top: 70, rot: -6, c: "#f472b6" },
        { left: 70, top: 130, rot: 14, c: "#fbbf24" },
        { left: 160, top: 140, rot: -18, c: "#a78bfa" },
        { left: 240, top: 110, rot: 4, c: "#fb7185" },
        { left: 300, top: 60, rot: -10, c: "#2dd4bf" },
        { left: 280, top: 150, rot: 16, c: "#f97316" },
    ];
    return (
        <WoodSurface
            sx={{
                width: wide ? 480 : 420,
                height: wide ? 320 : 280,
                borderRadius: 3,
                position: "relative",
                border: "4px solid #3e2723",
                boxShadow:
                    "0 28px 48px rgba(0,0,0,0.4), inset 0 2px 0 rgba(255,255,255,0.12)",
                transform: "perspective(900px) rotateX(28deg) rotateZ(-4deg)",
                ...sx,
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 16,
                    borderRadius: 2,
                    border: "2px solid rgba(0,0,0,0.25)",
                    background: "rgba(62,39,35,0.35)",
                }}
            />
            {pieces.map((p, i) => (
                <Box
                    key={i}
                    sx={{
                        position: "absolute",
                        left: p.left,
                        top: p.top,
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        background: p.c,
                        transform: `rotate(${p.rot}deg)`,
                        boxShadow: "0 6px 12px rgba(0,0,0,0.35)",
                        border: "2px solid rgba(255,255,255,0.35)",
                    }}
                />
            ))}
        </WoodSurface>
    );
}

/** 03 — Sorting split. */
function ShowcaseSorting({ scene }) {
    const isSquare = useIsSquare();
    if (!isSquare) {
        return (
            <AdCanvas>
                <WoodSurface sx={{ position: "absolute", inset: 0, zIndex: 0 }} />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                        background:
                            "radial-gradient(ellipse at 55% 45%, rgba(255,255,255,0.14) 0%, transparent 55%)",
                        pointerEvents: "none",
                    }}
                />
                <AdHeadline
                    title="SMOOTH SORTING TRAYS"
                    sx={{ position: "absolute", top: 14, left: 0, right: 0, pt: 0, zIndex: 4 }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        left: 100,
                        top: "52%",
                        transform: "translateY(-50%)",
                        zIndex: 3,
                    }}
                >
                    <ShotPhone slug="03" rotate={-4} width={460} height={940} />
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        right: 36,
                        top: "50%",
                        transform: "translateY(-50%)",
                        zIndex: 4,
                        width: 480,
                    }}
                >
                    <SortingTrayFocal wide sx={{ mb: 2, mx: "auto" }} />
                    <FloatBadge
                        sx={{
                            width: "100%",
                            bgcolor: "rgba(255,255,255,0.96)",
                            border: "1px solid rgba(148,163,184,0.45)",
                            boxShadow:
                                "0 16px 36px rgba(0,0,0,0.35), 0 0 24px rgba(52,211,153,0.25)",
                        }}
                    >
                        <CategoryIcon sx={{ color: "#3BA666", fontSize: 40, mb: 0.5 }} />
                        <Typography sx={{ fontWeight: 900, fontSize: 26, color: "#0f172a" }}>
                            Edge · Color · Shape
                        </Typography>
                        <Typography sx={{ mt: 0.75, fontSize: 14, color: "#475569", lineHeight: 1.4 }}>
                            Organize pieces into trays so every board feels effortless.
                        </Typography>
                    </FloatBadge>
                </Box>
            </AdCanvas>
        );
    }

    return (
        <AdCanvas>
            <WoodSurface sx={{ position: "absolute", inset: 0, zIndex: 0 }} />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background:
                        "radial-gradient(ellipse at 50% 40%, rgba(255,255,255,0.12) 0%, transparent 55%)",
                    pointerEvents: "none",
                }}
            />
            <AdHeadline title="SMOOTH SORTING TRAYS" sx={{ pt: 3.5 }} />
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: 280,
                    transform: "translateX(-50%)",
                    zIndex: 3,
                }}
            >
                <SortingTrayFocal />
            </Box>
            <FloatBadge
                sx={{
                    position: "absolute",
                    right: 56,
                    top: 360,
                    width: 320,
                    bgcolor: "rgba(255,255,255,0.96)",
                    border: "1px solid rgba(148,163,184,0.45)",
                    boxShadow: "0 16px 36px rgba(0,0,0,0.35), 0 0 24px rgba(52,211,153,0.25)",
                }}
            >
                <CategoryIcon sx={{ color: "#3BA666", fontSize: 40, mb: 0.5 }} />
                <Typography sx={{ fontWeight: 900, fontSize: 26, color: "#0f172a" }}>
                    Edge · Color · Shape
                </Typography>
                <Typography sx={{ mt: 0.75, fontSize: 14, color: "#475569", lineHeight: 1.4 }}>
                    Organize pieces into trays so every board feels effortless.
                </Typography>
            </FloatBadge>
            <GlowConnector x1={700} y1={420} x2={860} y2={420} />
            <Box sx={{ position: "absolute", left: 40, bottom: -100, zIndex: 4 }}>
                <ShotPhone slug="03" rotate={-5} width={450} height={930} />
            </Box>
        </AdCanvas>
    );
}

/** 04 — Journey map. */
function ShowcaseJourney({ scene }) {
    const isSquare = useIsSquare();
    if (!isSquare) {
        return (
            <AdCanvas>
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 0,
                        backgroundImage: `url(${JOURNEY_MAP_BG})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        filter: "saturate(1.08) contrast(1.05)",
                    }}
                />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                        background: `
                            radial-gradient(ellipse at 30% 20%, rgba(217,119,6,0.22) 0%, transparent 45%),
                            linear-gradient(90deg, rgba(28,25,23,0.45) 0%, rgba(41,37,36,0.2) 55%, rgba(28,25,23,0.4) 100%)
                        `,
                        pointerEvents: "none",
                    }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        top: 24,
                        left: "50%",
                        transform: "translateX(-50%)",
                        zIndex: 4,
                        px: 3.5,
                        py: 1.1,
                        borderRadius: 2,
                        background: "linear-gradient(180deg, #fde68a 0%, #d97706 100%)",
                        border: "2px solid #78350f",
                        boxShadow: "0 8px 20px rgba(0,0,0,0.35)",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: TITLE_STACK,
                            fontWeight: 900,
                            fontSize: 26,
                            color: "#431407",
                            letterSpacing: "0.03em",
                            whiteSpace: "nowrap",
                        }}
                    >
                        EXPLORE THE WORLD OF PUZZLES
                    </Typography>
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        right: 56,
                        top: "46%",
                        transform: "translateY(-50%)",
                        zIndex: 3,
                        width: 170,
                        height: 170,
                        borderRadius: "50%",
                        background:
                            "radial-gradient(circle at 35% 30%, #fde68a 0%, #d97706 45%, #78350f 100%)",
                        border: "5px solid #fef3c7",
                        boxShadow:
                            "0 16px 32px rgba(0,0,0,0.5), 0 0 28px rgba(251,191,36,0.4)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <ExploreIcon sx={{ fontSize: 88, color: "#431407" }} />
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        left: "42%",
                        top: "55%",
                        transform:
                            "translate(-50%, -50%) perspective(900px) rotateX(10deg) rotateZ(-5deg)",
                        zIndex: 4,
                    }}
                >
                    <ShotPhone slug="04" width={440} height={900} />
                </Box>
            </AdCanvas>
        );
    }

    return (
        <AdCanvas>
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    backgroundImage: `url(${JOURNEY_MAP_BG})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    filter: "saturate(1.05) contrast(1.05)",
                }}
            />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: `
                        radial-gradient(ellipse at 30% 20%, rgba(217,119,6,0.2) 0%, transparent 45%),
                        linear-gradient(180deg, rgba(28,25,23,0.35) 0%, rgba(41,37,36,0.55) 100%)
                    `,
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "absolute",
                    top: 48,
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 4,
                    px: 4,
                    py: 1.25,
                    borderRadius: 2,
                    background: "linear-gradient(180deg, #fde68a 0%, #d97706 100%)",
                    border: "2px solid #78350f",
                    boxShadow: "0 10px 28px rgba(0,0,0,0.4)",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 30,
                        color: "#431407",
                        letterSpacing: "0.03em",
                        whiteSpace: "nowrap",
                    }}
                >
                    EXPLORE THE WORLD OF PUZZLES
                </Typography>
            </Box>
            <Box
                sx={{
                    position: "absolute",
                    right: 40,
                    top: 160,
                    zIndex: 3,
                    width: 220,
                    height: 220,
                    borderRadius: "50%",
                    background:
                        "radial-gradient(circle at 35% 30%, #fde68a 0%, #d97706 45%, #78350f 100%)",
                    border: "6px solid #fef3c7",
                    boxShadow:
                        "0 20px 40px rgba(0,0,0,0.5), 0 0 36px rgba(251,191,36,0.45)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <ExploreIcon sx={{ fontSize: 110, color: "#431407" }} />
            </Box>
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: -140,
                    transform:
                        "translateX(-50%) perspective(900px) rotateX(12deg) rotateZ(-6deg)",
                    zIndex: 4,
                }}
            >
                <ShotPhone slug="04" width={460} height={940} />
            </Box>
        </AdCanvas>
    );
}

/** 05 — Giant grid. */
function ShowcaseGiant({ scene }) {
    const isSquare = useIsSquare();
    if (!isSquare) {
        return (
            <AdCanvas>
                <SceneBleed slug="05" dim={0.12} />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                        background:
                            "linear-gradient(90deg, rgba(2,6,23,0.72) 0%, rgba(2,6,23,0.35) 42%, rgba(2,6,23,0.15) 100%)",
                        pointerEvents: "none",
                    }}
                />
                <AdHeadline
                    title={"MASTER THE GIANT 1024 GRID"}
                    sx={{ position: "absolute", top: 18, left: 0, right: 0, pt: 0, zIndex: 4 }}
                />
                <Box
                    sx={{
                        position: "absolute",
                        left: 80,
                        top: "54%",
                        transform: "translateY(-50%)",
                        zIndex: 3,
                    }}
                >
                    <ShotPhone slug="05" rotate={-2} width={480} height={980} />
                </Box>
                <FloatBadge
                    sx={{
                        position: "absolute",
                        right: 48,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 420,
                        textAlign: "center",
                        py: 3,
                        zIndex: 4,
                        background:
                            "linear-gradient(165deg, rgba(2,6,23,0.9) 0%, rgba(15,23,42,0.78) 100%)",
                        border: "1px solid rgba(110,231,183,0.5)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.45)",
                    }}
                >
                    <GridOnIcon sx={{ fontSize: 48, color: "#6ee7b7" }} />
                    <Typography
                        sx={{
                            mt: 1,
                            fontFamily: TITLE_STACK,
                            fontWeight: 900,
                            fontSize: 36,
                            color: "#ecfdf5",
                            letterSpacing: "0.04em",
                        }}
                    >
                        1024 PIECES
                    </Typography>
                    <Typography sx={{ mt: 1, fontSize: 14, color: "rgba(226,232,240,0.88)" }}>
                        Immersive giant boards for deep focus sessions.
                    </Typography>
                </FloatBadge>
            </AdCanvas>
        );
    }

    return (
        <AdCanvas>
            <SceneBleed slug="05" dim={0.28} />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background:
                        "linear-gradient(135deg, transparent 40%, rgba(2,6,23,0.72) 78%)",
                    pointerEvents: "none",
                }}
            />
            <FloatBadge
                sx={{
                    position: "absolute",
                    right: 48,
                    top: 80,
                    width: 420,
                    textAlign: "center",
                    py: 3,
                    zIndex: 4,
                    bgcolor: "rgba(2,6,23,0.78)",
                    background:
                        "linear-gradient(165deg, rgba(2,6,23,0.88) 0%, rgba(15,23,42,0.72) 100%)",
                    border: "1px solid rgba(110,231,183,0.5)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.45)",
                }}
            >
                <GridOnIcon sx={{ fontSize: 48, color: "#6ee7b7" }} />
                <Typography
                    sx={{
                        mt: 1,
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 42,
                        color: "#ecfdf5",
                        letterSpacing: "0.04em",
                    }}
                >
                    1024 PIECES
                </Typography>
                <Typography sx={{ mt: 1, fontSize: 15, color: "rgba(226,232,240,0.88)" }}>
                    Immersive giant boards for deep focus sessions.
                </Typography>
            </FloatBadge>
            <Box
                sx={{
                    position: "absolute",
                    left: 80,
                    bottom: -180,
                    zIndex: 3,
                    maskImage: "linear-gradient(180deg, #000 55%, transparent 100%)",
                    WebkitMaskImage: "linear-gradient(180deg, #000 55%, transparent 100%)",
                }}
            >
                <ShotPhone slug="05" rotate={-3} width={480} height={980} />
            </Box>
        </AdCanvas>
    );
}

/** 06 — Daily + streak. */
function ShowcaseDaily({ scene }) {
    const isSquare = useIsSquare();
    const particles = [...Array(isSquare ? 14 : 20)].map((_, i) => ({
        left: `${(i * 37 + 8) % 92}%`,
        top: `${(i * 29 + 5) % 90}%`,
        size: 10 + (i % 5) * 6,
        rot: (i * 23) % 40 - 20,
        opacity: 0.2 + (i % 4) * 0.08,
    }));

    if (!isSquare) {
        return (
            <AdCanvas>
                <ShellFill scene={scene} />
                {particles.slice(0, 12).map((p, i) => (
                    <Box
                        key={i}
                        aria-hidden
                        sx={{
                            position: "absolute",
                            zIndex: 1,
                            left: p.left,
                            top: p.top,
                            width: p.size,
                            height: p.size,
                            borderRadius: 0.75,
                            background:
                                i % 2 === 0
                                    ? "linear-gradient(145deg,#c084fc,#7c3aed)"
                                    : "linear-gradient(145deg,#f0abfc,#a855f7)",
                            border: "1px solid rgba(255,255,255,0.25)",
                            opacity: p.opacity,
                            transform: `rotate(${p.rot}deg)`,
                            pointerEvents: "none",
                        }}
                    />
                ))}
                <AdHeadline
                    title="DAILY MASTERPIECE CHALLENGE"
                    sx={{ position: "absolute", top: 14, left: 0, right: 0, pt: 0, zIndex: 4 }}
                />
                <GlowConnector x1={520} y1={320} x2={700} y2={300} />
                <Box
                    sx={{
                        position: "absolute",
                        left: 120,
                        top: "54%",
                        transform: "translateY(-50%)",
                        zIndex: 3,
                    }}
                >
                    <ShotPhone slug="06" rotate={-3} width={460} height={940} />
                </Box>
                <Box
                    sx={{
                        position: "absolute",
                        right: 40,
                        top: "52%",
                        transform: "translateY(-50%)",
                        zIndex: 4,
                    }}
                >
                    <StreakTrophyCard wide />
                </Box>
            </AdCanvas>
        );
    }

    return (
        <AdCanvas>
            <ShellFill scene={scene} />
            {particles.map((p, i) => (
                <Box
                    key={i}
                    aria-hidden
                    sx={{
                        position: "absolute",
                        zIndex: 1,
                        left: p.left,
                        top: p.top,
                        width: p.size,
                        height: p.size,
                        borderRadius: 0.75,
                        background:
                            i % 2 === 0
                                ? "linear-gradient(145deg,#c084fc,#7c3aed)"
                                : "linear-gradient(145deg,#f0abfc,#a855f7)",
                        border: "1px solid rgba(255,255,255,0.25)",
                        opacity: p.opacity,
                        transform: `rotate(${p.rot}deg)`,
                        boxShadow: "0 4px 10px rgba(0,0,0,0.25)",
                        pointerEvents: "none",
                    }}
                />
            ))}
            <AdHeadline title="DAILY MASTERPIECE CHALLENGE" sx={{ pt: 3.5 }} />
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: -120,
                    transform: "translateX(-50%)",
                    zIndex: 3,
                }}
            >
                <ShotPhone slug="06" rotate={-2} width={470} height={960} />
            </Box>
            <FloatBadge
                sx={{
                    position: "absolute",
                    left: "54%",
                    top: 280,
                    width: 340,
                    textAlign: "center",
                    py: 2.5,
                    zIndex: 5,
                    boxShadow:
                        "0 18px 40px rgba(0,0,0,0.45), 0 0 32px rgba(251,191,36,0.45)",
                    border: "1px solid rgba(251,191,36,0.55)",
                }}
            >
                <EmojiEventsIcon
                    sx={{
                        fontSize: 56,
                        color: "#fbbf24",
                        filter: "drop-shadow(0 0 12px rgba(251,191,36,0.8))",
                    }}
                />
                <Typography
                    sx={{
                        mt: 1,
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 30,
                        color: "#fde68a",
                    }}
                >
                    7 Day Streak!
                </Typography>
                <Stack
                    direction="row"
                    spacing={0.75}
                    justifyContent="center"
                    alignItems="center"
                    sx={{ mt: 1 }}
                >
                    <LocalFireDepartmentIcon sx={{ color: "#f97316" }} />
                    <Typography sx={{ fontWeight: 700, color: "#e2e8f0", fontSize: 15 }}>
                        Fresh art every day
                    </Typography>
                </Stack>
            </FloatBadge>
        </AdCanvas>
    );
}

function VaultFanCard({ title, imageUrl, tint, sx = {} }) {
    return (
        <Box
            sx={{
                width: 220,
                height: 300,
                borderRadius: 2.5,
                overflow: "hidden",
                background: tint || "#1e293b",
                border: "2px solid rgba(255,255,255,0.35)",
                boxShadow: "0 18px 36px rgba(0,0,0,0.35)",
                position: "relative",
                ...sx,
            }}
        >
            {imageUrl ? (
                <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                    }}
                />
            ) : null}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(180deg, transparent 42%, rgba(15,23,42,0.72) 100%)",
                    pointerEvents: "none",
                }}
            />
            <Typography
                sx={{
                    position: "absolute",
                    left: 14,
                    right: 14,
                    bottom: 14,
                    m: 0,
                    fontFamily: TITLE_STACK,
                    fontWeight: 900,
                    fontSize: 16,
                    color: "#fff",
                    textShadow: "0 2px 8px rgba(0,0,0,0.55)",
                    zIndex: 1,
                }}
            >
                {title}
            </Typography>
        </Box>
    );
}

/** 07 — Vault unlock. */
function ShowcaseVault({ scene }) {
    const isSquare = useIsSquare();
    const covers = useVaultPackCovers(scene);
    const landscapeLayout = [
        { rot: -10, z: 2 },
        { rot: 0, z: 4 },
        { rot: 10, z: 2 },
    ];
    const packs =
        covers.length > 0
            ? covers
            : VAULT_PACK_FALLBACKS.map((title, i) => ({
                  title,
                  imageUrl: null,
                  tint: [
                      "linear-gradient(160deg,#7c3aed 0%,#1e1b4b 100%)",
                      "linear-gradient(160deg,#be123c 0%,#431407 100%)",
                      "linear-gradient(160deg,#0ea5e9 0%,#0c4a6e 100%)",
                  ][i],
              }));

    if (!isSquare) {
        return (
            <AdCanvas>
                <ShellFill scene={scene} />
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 1,
                        background:
                            "radial-gradient(ellipse at 50% 60%, rgba(59,166,102,0.14) 0%, transparent 50%)",
                        pointerEvents: "none",
                    }}
                />
                <AdHeadline
                    title="UNLOCK 1000+ MASTERPIECES"
                    dark
                    sx={{ position: "absolute", top: 16, left: 0, right: 0, pt: 0, zIndex: 5 }}
                />
                <FloatBadge
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: 78,
                        transform: "translateX(-50%)",
                        zIndex: 5,
                        px: 2.5,
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <StarsIcon sx={{ color: "#fbbf24", fontSize: 24 }} />
                    <Typography sx={{ fontWeight: 900, fontSize: 16, color: "#ecfdf5" }}>
                        Fresh Vault Ready
                    </Typography>
                </FloatBadge>
                <Box
                    sx={{
                        position: "absolute",
                        left: "50%",
                        top: "56%",
                        transform: "translate(-50%, -50%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2.5,
                        zIndex: 3,
                        perspective: "1200px",
                    }}
                >
                    {packs.map((p, i) => {
                        const layout = landscapeLayout[i] || landscapeLayout[0];
                        return (
                            <VaultFanCard
                                key={`${p.title}-${i}`}
                                title={p.title}
                                imageUrl={p.imageUrl}
                                tint={p.tint}
                                sx={{
                                    width: 300,
                                    height: 420,
                                    zIndex: layout.z,
                                    transform: `rotateY(${layout.rot * 1.2}deg) rotateZ(${layout.rot}deg) translateY(${
                                        layout.rot === 0 ? -12 : 18
                                    }px)`,
                                }}
                            />
                        );
                    })}
                </Box>
            </AdCanvas>
        );
    }

    const leftPack = packs[0];
    const rightPack = packs[2] || packs[1] || packs[0];

    return (
        <AdCanvas>
            <ShellFill scene={scene} />
            <Box
                aria-hidden
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 1,
                    background: `
                        radial-gradient(ellipse at 50% 70%, rgba(59,166,102,0.12) 0%, transparent 45%),
                        url(${FEATURE_GRAPHIC})
                    `,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    opacity: 0.18,
                    pointerEvents: "none",
                    mixBlendMode: "multiply",
                }}
            />
            <AdHeadline title="UNLOCK 1000+ MASTERPIECES" dark sx={{ pt: 3 }} />
            {leftPack ? (
                <VaultFanCard
                    title={leftPack.title}
                    imageUrl={leftPack.imageUrl}
                    tint={leftPack.tint}
                    sx={{
                        position: "absolute",
                        left: 90,
                        top: 340,
                        zIndex: 2,
                        transform: "rotate(-14deg) translateY(20px)",
                    }}
                />
            ) : null}
            {rightPack ? (
                <VaultFanCard
                    title={rightPack.title}
                    imageUrl={rightPack.imageUrl}
                    tint={rightPack.tint}
                    sx={{
                        position: "absolute",
                        right: 90,
                        top: 340,
                        zIndex: 2,
                        transform: "rotate(14deg) translateY(20px)",
                    }}
                />
            ) : null}
            <FloatBadge
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: 200,
                    transform: "translateX(-50%)",
                    zIndex: 5,
                    px: 3,
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "rgba(15,23,42,0.9)",
                    boxShadow: "0 12px 28px rgba(0,0,0,0.35), 0 0 20px rgba(251,191,36,0.3)",
                }}
            >
                <StarsIcon sx={{ color: "#fbbf24", fontSize: 28 }} />
                <Typography sx={{ fontWeight: 900, fontSize: 20, color: "#ecfdf5" }}>
                    Fresh Vault Ready
                </Typography>
            </FloatBadge>
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: -100,
                    transform: "translateX(-50%)",
                    zIndex: 4,
                }}
            >
                <ShotPhone slug="07" width={460} height={940} />
            </Box>
        </AdCanvas>
    );
}

/** 08 — Gallery / CTA. */
function ShowcaseGallery({ scene }) {
    const isSquare = useIsSquare();
    const featurePills = [
        { label: "Offline Play", Icon: ExtensionIcon },
        { label: "No Time Limits", Icon: CategoryIcon },
        { label: "HD Art", Icon: StarsIcon },
        { label: "Color Baskets", Icon: PaletteIcon },
    ];
    if (!isSquare) {
        return (
            <AdCanvas sx={{ bgcolor: "#0f172a" }}>
                <SceneBleed slug="08" dim={0.08} grid />
                {/* 3×2 window frames over the live gallery art */}
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 8,
                        zIndex: 2,
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gridTemplateRows: "repeat(2, 1fr)",
                        gap: 8,
                        pointerEvents: "none",
                    }}
                >
                    {[...Array(6)].map((_, i) => (
                        <Box
                            key={i}
                            sx={{
                                borderRadius: 1.5,
                                boxShadow: "inset 0 0 0 2px rgba(248,250,252,0.28)",
                                background: "transparent",
                            }}
                        />
                    ))}
                </Box>
                <Box
                    aria-hidden
                    sx={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 3,
                        background:
                            "linear-gradient(180deg, rgba(2,6,23,0.62) 0%, transparent 30%, transparent 70%, rgba(2,6,23,0.7) 100%)",
                        pointerEvents: "none",
                    }}
                />
                <Stack
                    direction="row"
                    spacing={1.25}
                    justifyContent="center"
                    sx={{
                        position: "absolute",
                        left: 32,
                        right: 32,
                        top: 20,
                        zIndex: 5,
                    }}
                >
                    {featurePills.map(({ label, Icon }) => (
                        <FloatBadge
                            key={label}
                            sx={{
                                py: 0.9,
                                px: 1.5,
                                flex: 1,
                                bgcolor: "rgba(15,23,42,0.92)",
                            }}
                        >
                            <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <Icon sx={{ color: "#6ee7b7", fontSize: 18 }} />
                                <Typography sx={{ fontWeight: 800, fontSize: 13, color: "#e0f2fe" }}>
                                    {label}
                                </Typography>
                            </Stack>
                        </FloatBadge>
                    ))}
                </Stack>
                <Box
                    sx={{
                        position: "absolute",
                        left: "50%",
                        bottom: 22,
                        transform: "translateX(-50%)",
                        zIndex: 6,
                        px: 4.5,
                        py: 1.15,
                        borderRadius: 999,
                        background: "linear-gradient(90deg, #059669, #34d399, #6ee7b7)",
                        boxShadow: "0 8px 24px rgba(16,185,129,0.5)",
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: TITLE_STACK,
                            fontWeight: 900,
                            fontSize: 22,
                            color: "#064e3b",
                            letterSpacing: "0.06em",
                        }}
                    >
                        PLAY NOW
                    </Typography>
                </Box>
            </AdCanvas>
        );
    }

    return (
        <AdCanvas sx={{ bgcolor: "#ffffff" }}>
            <ShellFill scene={scene} />
            {/* Live gallery artwork fills ~80% of the square */}
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    top: 110,
                    transform: "translateX(-50%)",
                    width: 980,
                    height: 980,
                    zIndex: 2,
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 16px 40px rgba(15,23,42,0.18)",
                    border: "1px solid rgba(15,23,42,0.08)",
                }}
            >
                <SceneBleed slug="08" dim={0.05} grid />
            </Box>
            <Stack
                direction="row"
                spacing={1.25}
                justifyContent="center"
                sx={{
                    position: "absolute",
                    left: 40,
                    right: 40,
                    top: 28,
                    zIndex: 5,
                }}
            >
                {featurePills.map(({ label, Icon }) => (
                    <FloatBadge
                        key={label}
                        sx={{
                            py: 1,
                            px: 1.75,
                            flex: 1,
                            bgcolor: "rgba(15,23,42,0.92)",
                        }}
                    >
                        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
                            <Icon sx={{ color: "#6ee7b7", fontSize: 20 }} />
                            <Typography sx={{ fontWeight: 800, fontSize: 14, color: "#e0f2fe" }}>
                                {label}
                            </Typography>
                        </Stack>
                    </FloatBadge>
                ))}
            </Stack>
            <Box
                sx={{
                    position: "absolute",
                    left: "50%",
                    bottom: 36,
                    transform: "translateX(-50%)",
                    zIndex: 6,
                    px: 5,
                    py: 1.35,
                    borderRadius: 999,
                    background: "linear-gradient(90deg, #059669, #34d399, #6ee7b7)",
                    boxShadow: "0 10px 28px rgba(16,185,129,0.55)",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: TITLE_STACK,
                        fontWeight: 900,
                        fontSize: 24,
                        color: "#064e3b",
                        letterSpacing: "0.06em",
                    }}
                >
                    PLAY NOW
                </Typography>
            </Box>
        </AdCanvas>
    );
}

/**
 * Jigsaw Muse Google Ads showcase creatives (square 1200×1200 or landscape 1200×628).
 */
export default function JigsawShowcaseGoogleAdCreative({
    scene,
    lang = "en-US",
    variant = "square",
}) {
    const slug = scene?.slug || "01";
    let content = null;
    switch (slug) {
        case "01":
            content = <ShowcaseHero scene={scene} />;
            break;
        case "02":
            content = <ShowcaseColorBasket scene={scene} />;
            break;
        case "03":
            content = <ShowcaseSorting scene={scene} />;
            break;
        case "04":
            content = <ShowcaseJourney scene={scene} />;
            break;
        case "05":
            content = <ShowcaseGiant scene={scene} />;
            break;
        case "06":
            content = <ShowcaseDaily scene={scene} />;
            break;
        case "07":
            content = <ShowcaseVault scene={scene} />;
            break;
        case "08":
            content = <ShowcaseGallery scene={scene} />;
            break;
        default:
            content = <ShowcaseHero scene={scene} />;
            break;
    }

    return (
        <AdVariantContext.Provider value={variant === "landscape" ? "landscape" : "square"}>
            <AdLangContext.Provider value={lang}>{content}</AdLangContext.Provider>
        </AdVariantContext.Provider>
    );
}
